import type { ApiErrorBody } from "../types/api";

// Same-origin by default: <base>api/v1, proxied to FastAPI in development and by
// nginx in production. VITE_API_BASE overrides it for split deployments.
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? `${import.meta.env.BASE_URL}api/v1`;

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly field: string | null;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(options: {
    code: string;
    status: number;
    message: string;
    field?: string | null;
    details?: Record<string, unknown>;
    requestId?: string | null;
  }) {
    super(options.message);
    this.name = "ApiError";
    this.code = options.code;
    this.status = options.status;
    this.field = options.field ?? null;
    this.details = options.details ?? {};
    this.requestId = options.requestId ?? null;
  }
}

export const NETWORK_ERROR_MESSAGE = "We can't reach FreightAudit right now. Check your connection and try again.";

export function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  return new ApiError({ code: "UNEXPECTED", status: 0, message: "Something unexpected happened. Please try again." });
}

function parseBody(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === "object" && value !== null && "error" in value;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      credentials: "same-origin",
      ...init,
      headers: { Accept: "application/json", ...init.headers },
    });
  } catch (error) {
    if (isAbort(error)) throw error;
    throw new ApiError({ code: "NETWORK_ERROR", status: 0, message: NETWORK_ERROR_MESSAGE });
  }
  if (response.status === 204) return undefined as T;
  const body = parseBody(await response.text());
  if (!response.ok) {
    const error = isErrorBody(body) ? body.error : null;
    throw new ApiError({
      code: error?.code ?? `HTTP_${response.status}`,
      status: response.status,
      message: error?.message ?? `The server answered ${response.status}. Please try again.`,
      field: error?.field ?? null,
      details: error?.details ?? {},
      requestId: response.headers.get("x-request-id"),
    });
  }
  return body as T;
}

export function jsonBody(value: unknown): RequestInit {
  return { body: JSON.stringify(value), headers: { "Content-Type": "application/json" } };
}
